// frontend/src/pages/looks/LookDetailPage.js
// Public landing page for a single Look (/look/:lookId). External social links
// land here; visitors see the result, the professional, and can book the
// service that produced it. Styled to match the OGESTYLE brand (see
// ProductDetail / ProfessionalProfile for the design language).

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { FaEye, FaChevronLeft, FaChevronRight, FaRegComment, FaClock, FaTags } from "react-icons/fa";
import ShareButtons from "../../components/ShareButtons";
import { resolveLookShareUrl, resolveLookShareImageUrl } from "../../utils/lookShareUtils";
import LikesButton from "../../components/LikesButton";
import CommentsSection from "../../components/CommentsSection";
import FollowButton from "../../components/looks/FollowButton";
import SaveButton from "../../components/looks/SaveButton";
import RepostButton from "../../components/looks/RepostButton";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";

const COLOR = {
  text: "#0f172a",
  muted: "#6b7280",
  faint: "#64748b",
  blue: "#1d4f80",
  link: "#2563eb",
  green: "#059669",
  border: "#edf1f6",
};

function LookDetailPage() {
  const { lookId } = useParams();
  const navigate = useNavigate();
  const [look, setLook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeImage, setActiveImage] = useState(0);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 768,
  );
  const commentsRef = useRef(null);
  const touchStartX = useRef(null);

  const apiBase =
    process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

  const loadLook = useCallback(async () => {
    if (!lookId) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${apiBase}/api/looks/${lookId}/public`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Look not found");
      setLook(data.look);
      setActiveImage(0);
    } catch (err) {
      setError(err.message || "Unable to load look");
    } finally {
      setLoading(false);
    }
  }, [apiBase, lookId]);

  useEffect(() => {
    loadLook();
  }, [loadLook]);

  // Classic mobile layout: track viewport so the hero stacks on small screens.
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Keyboard navigation for the gallery (left/right arrows)
  useEffect(() => {
    if (!look || !Array.isArray(look.images) || look.images.length < 2) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") {
        setActiveImage((i) => (i - 1 + look.images.length) % look.images.length);
      } else if (e.key === "ArrowRight") {
        setActiveImage((i) => (i + 1) % look.images.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [look]);

  // Record a view on mount (server dedupes per viewer per day via LookViewEvent).
  useEffect(() => {
    if (!look?._id) return;
    let viewerId = localStorage.getItem("ogestyle_look_viewer_id");
    if (!viewerId) {
      viewerId = `lv-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem("ogestyle_look_viewer_id", viewerId);
    }
    const headers = { "x-viewer-id": viewerId };
    const token = localStorage.getItem("ogestyle_token");
    if (token) headers.Authorization = `Bearer ${token}`;
    fetch(`${apiBase}/api/looks/${look._id}/view`, {
      method: "POST",
      headers,
    }).catch(() => {});
  }, [look?._id, apiBase]);

  if (loading) {
    return (
      <div style={{ background: "#fff", minHeight: "100vh" }}>
        <CustomerHeader />
        <div
          style={{
            minHeight: "55vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: COLOR.faint,
            fontSize: "15px",
          }}
        >
          Loading look…
        </div>
        <CustomerFooter />
      </div>
    );
  }

  if (error || !look) {
    return (
      <div style={{ background: "#fff", minHeight: "100vh" }}>
        <CustomerHeader />
        <div
          style={{
            minHeight: "55vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
            color: COLOR.faint,
          }}
        >
          <p style={{ fontSize: "20px", color: COLOR.text }}>
            {error || "Look not found"}
          </p>
          <a href="/" style={{ color: COLOR.link }}>
            Back to OGESTYLE
          </a>
        </div>
        <CustomerFooter />
      </div>
    );
  }

  const images =
    Array.isArray(look.images) && look.images.length > 0 ? look.images : [];
  const serviceName = look.bookingSnapshot?.serviceName || "Beauty Look";
  const shareUrl = resolveLookShareUrl(look._id, look.cardVersion);
  const staffName = look.staffName || "Professional";
  const businessName = look.businessName || "";
  const roleLabel =
    look.staffRole === "R4"
      ? "Freelancer"
      : look.staffRole === "R7"
        ? "Booth Renter"
        : "Professional";
  const initials =
    staffName
      .split(" ")
      .map((s) => s[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "OG";

  const renderPrice = () => {
    const pt = look.priceType || "from";
    if (pt === "variable") return "Price varies";
    if (pt === "hourly" && look.price)
      return `$${Number(look.price).toFixed(2)}/hr`;
    if (pt === "range" && look.maxPrice && look.price)
      return `$${Number(look.price).toFixed(2)} – $${Number(look.maxPrice).toFixed(2)}`;
    if (pt === "fixed" && look.price) return `$${Number(look.price).toFixed(2)}`;
    return look.price ? `$${Number(look.price).toFixed(2)}` : null;
  };
  const priceLabel = renderPrice();

  const handleBook = () => {
    const params = new URLSearchParams();
    if (look.staffId) params.set("staffId", String(look.staffId));
    if (look.serviceId) params.set("serviceId", String(look.serviceId));
    if (look._id) params.set("lookId", String(look._id));
    if (look.shopId) params.set("shopId", String(look.shopId));
    const isFreelancer = look.staffRole === "R4" || look.staffRole === "R7";
    navigate(
      `${isFreelancer ? "/freelancer-booking" : "/booking"}?${params.toString()}`,
    );
  };

  const openLook = (id) => navigate(`/look/${id || ""}`);

  const prevImage = () =>
    images.length > 1 &&
    setActiveImage((i) => (i - 1 + images.length) % images.length);
  const nextImage = () =>
    images.length > 1 && setActiveImage((i) => (i + 1) % images.length);

  // Tap-to-advance (tap left half = previous, right half = next) and swipe on
  // touch devices — the arrows are kept subtle on desktop only.
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? null;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx =
      (e.changedTouches?.[0]?.clientX ?? touchStartX.current) -
      touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) > 40) {
      if (dx < 0) nextImage();
      else prevImage();
    }
  };
  const handleImageTap = (e) => {
    if (images.length <= 1) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x < rect.width / 2) prevImage();
    else nextImage();
  };

  // ---- Reusable blocks (mobile row order differs from desktop) ----
  const shareButtons = (
    <ShareButtons
      url={shareUrl}
      imageUrl={resolveLookShareImageUrl(look._id, look.cardVersion)}
      title={serviceName}
      text={`Book ${serviceName} at ${businessName || "OGESTYLE"}${businessName ? " on OGESTYLE" : ""}`}
    />
  );

  const imageBlock = (
    <div>
      <div
        onClick={handleImageTap}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{
          position: "relative",
          border: `1px solid ${COLOR.border}`,
          borderRadius: "18px",
          background: "#0f172a",
          padding: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 14px 30px rgba(15,23,42,0.06)",
          cursor: images.length > 1 ? "pointer" : "default",
          userSelect: "none",
          WebkitUserSelect: "none",
          touchAction: "pan-y",
        }}
      >
        {images.length > 0 ? (
          <img
            src={images[activeImage].url}
            alt={serviceName}
            style={{
              // Show the full photo (no cropping) but cap the height so it
              // doesn't dominate the screen; natural (4:5) aspect is preserved.
              display: "block",
              margin: "0 auto",
              maxWidth: "100%",
              maxHeight: "min(65vh, 520px)",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              borderRadius: "12px",
            }}
          />
        ) : (
          <div
            style={{
              height: "320px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: COLOR.faint,
            }}
          >
            No image
          </div>
        )}
        {!isMobile && images.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
              aria-label="Previous image"
              style={{
                position: "absolute",
                left: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.6)",
                color: COLOR.blue,
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "13px",
                opacity: 0.75,
                boxShadow: "0 2px 8px rgba(15,23,42,0.12)",
              }}
            >
              <FaChevronLeft />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              aria-label="Next image"
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.6)",
                color: COLOR.blue,
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "13px",
                opacity: 0.75,
                boxShadow: "0 2px 8px rgba(15,23,42,0.12)",
              }}
            >
              <FaChevronRight />
            </button>
            <span
              style={{
                position: "absolute",
                bottom: "12px",
                left: "50%",
                transform: "translateX(-50%)",
                background: "rgba(15,23,42,0.65)",
                color: "#fff",
                padding: "4px 10px",
                borderRadius: "999px",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              {activeImage + 1} / {images.length}
            </span>
          </>
        )}
      </div>
    </div>
  );

  const titleBlock = (
    <h1
      style={{
        margin: "0",
        fontSize: isMobile ? "16px" : "28px",
        fontWeight: 700,
        color: COLOR.text,
        lineHeight: 1.3,
        textAlign: isMobile ? "center" : "left",
      }}
    >
      {serviceName}
    </h1>
  );

  // Canonical path from the API: /business/<biz>/<loc>/professional/<slug> for R3,
  // /freelancer/<slug> for R4 and R7. The flat legacy path is only a last resort.
  const proUrl =
    look.staffProfilePath ||
    (look.staffSlug ? `/professional/${look.staffSlug}` : null);
  const proInner = (
    <>
      {look.staffProfileImage ? (
        <img
          src={look.staffProfileImage}
          alt={staffName}
          style={{
            width: isMobile ? "38px" : "42px",
            height: isMobile ? "38px" : "42px",
            borderRadius: "50%",
            objectFit: "cover",
            flexShrink: 0,
          }}
        />
      ) : (
        <div
          style={{
            width: isMobile ? "38px" : "42px",
            height: isMobile ? "38px" : "42px",
            borderRadius: "50%",
            background: COLOR.blue,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: isMobile ? "14px" : "16px",
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {initials}
        </div>
      )}
      <div style={{ textAlign: "left" }}>
        <div
          style={{
            fontSize: isMobile ? "14px" : "15px",
            fontWeight: 600,
            color: proUrl ? COLOR.link : COLOR.text,
          }}
        >
          {staffName}
        </div>
        <div style={{ fontSize: isMobile ? "12px" : "13px", color: COLOR.muted }}>
          {businessName || roleLabel}
        </div>
      </div>
    </>
  );
  const proBlock = proUrl ? (
    <Link
      to={proUrl}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        justifyContent: isMobile ? "center" : "flex-start",
        textDecoration: "none",
        color: "inherit",
      }}
    >
      {proInner}
    </Link>
  ) : (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        justifyContent: isMobile ? "center" : "flex-start",
      }}
    >
      {proInner}
    </div>
  );

  const engagementRow = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: isMobile ? "center" : "flex-start",
        gap: isMobile ? "16px" : "20px",
        flexWrap: "wrap",
        padding: "2px 0",
      }}
    >
      <LikesButton lookId={look._id} apiBase={apiBase} initialLikes={look.likeCount || 0} />
      <SaveButton lookId={look._id} apiBase={apiBase} />
      <RepostButton lookId={look._id} apiBase={apiBase} />
      <span style={{ color: COLOR.faint, display: "inline-flex", alignItems: "center", gap: "5px", fontSize: isMobile ? "12px" : "13px" }}>
        <FaEye size={13} /> {(look.viewCount || 0).toLocaleString()} views
      </span>
      {isMobile ? (
        <button
          onClick={() => commentsRef.current?.openSheet()}
          aria-label="Open comments"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            border: "none",
            background: "transparent",
            color: COLOR.faint,
            fontSize: "12px",
            fontWeight: 600,
            cursor: "pointer",
            padding: "0",
          }}
        >
          <FaRegComment size={13} /> {(look.commentCount || 0).toLocaleString()}
        </button>
      ) : (
        shareButtons
      )}
    </div>
  );

  const socialRow = (
    <div style={{ padding: "2px 0" }}>{shareButtons}</div>
  );

  const durationSpan = look.durationMinutes ? (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontWeight: 600, color: COLOR.link, fontSize: isMobile ? "13px" : "14px" }}>
      <FaClock size={13} /> {look.durationMinutes} min
    </span>
  ) : null;

  const priceSpan = priceLabel ? (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontWeight: 700, color: COLOR.green, fontSize: isMobile ? "13px" : "14px" }}>
      <FaTags size={13} /> {priceLabel}
    </span>
  ) : null;

  const bookButton = (
    <button
      onClick={handleBook}
      style={{
        width: isMobile ? "100%" : "auto",
        marginLeft: isMobile ? "0" : "auto",
        padding: isMobile ? "12px 14px" : "12px 22px",
        background: "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
        color: "#fff",
        border: "none",
        borderRadius: "10px",
        fontSize: isMobile ? "14px" : "15px",
        fontWeight: 700,
        cursor: "pointer",
        whiteSpace: "nowrap",
        boxShadow: "0 8px 20px rgba(249,115,22,0.3)",
      }}
    >
      Book This Look
    </button>
  );

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      <CustomerHeader />

      <main
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          // Clear the fixed demo banner + fixed customer header.
          padding: isMobile
            ? "calc(var(--demo-banner-height, 0px) + 80px) 16px 48px"
            : "calc(var(--demo-banner-height, 0px) + 90px) 24px 64px",
        }}
      >
        {isMobile ? (
          <>
            {/* Mobile: image → engagement → title → pro → duration/price → book → social (no card wrapper) */}
            {imageBlock}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "14px",
                marginTop: "16px",
              }}
            >
              {engagementRow}
              {titleBlock}
              {proBlock}
              {look.staffId && (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <FollowButton staffId={look.staffId} apiBase={apiBase} />
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "16px",
                  flexWrap: "wrap",
                }}
              >
                {durationSpan}
                {priceSpan}
              </div>
              <div>{bookButton}</div>
              {socialRow}
            </div>
            <CommentsSection ref={commentsRef} lookId={look._id} apiBase={apiBase} isMobile />
          </>
        ) : (
          <>
            {/* Desktop header (left-aligned) */}
            <div style={{ marginBottom: "28px", textAlign: "left" }}>
              {titleBlock}
              <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: "18px", marginTop: "10px", flexWrap: "wrap" }}>
                {proBlock}
                {look.staffId && <FollowButton staffId={look.staffId} apiBase={apiBase} />}
              </div>
            </div>

            {/* Hero split: image + engagement + duration/book left, comments rail right */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1.05fr) minmax(0, 0.95fr)",
                gap: "30px",
                alignItems: "start",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                  minWidth: 0,
                }}
              >
                {imageBlock}
                <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
                  {engagementRow}
                  <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
                    {durationSpan}
                    {priceSpan}
                    {bookButton}
                  </div>
                </div>
              </div>

              {/* Comments rail (desktop): scrollable, same height as the image */}
              <div
                className="scroll-hidden"
                style={{
                  boxSizing: "border-box",
                  height: "550px",
                  overflowY: "auto",
                  WebkitOverflowScrolling: "touch",
                  border: `1px solid ${COLOR.border}`,
                  borderRadius: "18px",
                  background: "#fff",
                  boxShadow: "0 12px 28px rgba(15,23,42,0.05)",
                  padding: "18px 20px",
                }}
              >
                <CommentsSection
                  ref={commentsRef}
                  lookId={look._id}
                  apiBase={apiBase}
                  isMobile={false}
                  containerStyle={{ marginTop: 0 }}
                />
              </div>
            </div>
          </>
        )}

        {/* More looks by this professional */}
        {look.moreLooks && look.moreLooks.length > 0 && (
          <section style={{ marginTop: "40px" }}>
            <h2
              style={{
                margin: "0 0 16px 0",
                fontSize: isMobile ? "15px" : "18px",
                fontWeight: 700,
                color: COLOR.text,
              }}
            >
              More looks by {staffName}
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "repeat(2, minmax(0, 1fr))"
                  : "repeat(auto-fill, minmax(200px, 1fr))",
                gap: "16px",
              }}
            >
              {look.moreLooks.map((l) => (
                <div
                  key={l._id}
                  onClick={() => openLook(l.recordId || l._id)}
                  style={{
                    background: "#fff",
                    border: `1px solid ${COLOR.border}`,
                    borderRadius: "14px",
                    overflow: "hidden",
                    cursor: "pointer",
                    boxShadow: "0 4px 14px rgba(15,23,42,0.06)",
                    transition: "transform 0.15s",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.transform = "translateY(-3px)")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.transform = "none")
                  }
                >
                  <img
                    src={l.imageUrl}
                    alt={l.serviceName}
                    style={{
                      width: "100%",
                      // Same 4:5 portrait ratio as the lightbox so "More looks"
                      // thumbnails match the look photo dimensions.
                      aspectRatio: "4 / 5",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <div style={{ padding: isMobile ? "8px 10px" : "10px 12px" }}>
                    <div
                      style={{ fontSize: isMobile ? "12px" : "14px", fontWeight: 600, color: COLOR.text }}
                    >
                      {l.serviceName}
                    </div>
                    <div
                      style={{ fontSize: isMobile ? "11px" : "12px", color: COLOR.faint, marginTop: "2px" }}
                    >
                      {l.viewCount.toLocaleString()} views
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      <CustomerFooter />
    </div>
  );
}

export default LookDetailPage;
